import { useState } from 'react';
import { AtSign, Building2, Plus } from 'lucide-react';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { Modal } from '@/components/Modal';
import { Pill } from '@/components/StatusPill';
import { EmptyState, ErrorState, Skeleton } from '@/components/States';
import { useBrand, useBrands, useSaveSender } from '@/hooks/use-brands';
import { describeError } from '@/lib/describe-error';
import { Roles, type Session } from '@/auth/keycloak';
import type { BrandDetail, SenderIdentitySummary } from '@/lib/schemas';
import {
  BrandSelector,
  Checkbox,
  ReadOnlyNotice,
} from '@/pages/settings/settings-ui';
import {
  inputClass,
  useSelectedBrand,
} from '@/pages/settings/settings-shared';

/**
 * Sender addresses — the From lines a campaign may choose between.
 *
 * ── THE LOCAL PART ONLY ───────────────────────────────────────────────────────────
 *
 * You choose what goes before the @; the domain is the brand's and is not editable here. That is not
 * a simplification: an address on a domain this brand has not authenticated would send unsigned, and
 * making the domain a free-text field is how that happens by typo.
 *
 * ── RETIRED ADDRESSES ARE LISTED, NOT HIDDEN ──────────────────────────────────────
 *
 * A deactivated address is still attached to campaigns that were sent from it. Hiding it would make
 * an old campaign's From line unexplainable, so it stays in the list marked Retired — and the send
 * flow filters it out, which is where filtering belongs.
 */
export function SendersPage({ session }: { session: Session }) {
  const isAdministrator = session.roles.includes(Roles.Admin);

  const { data, isPending, isError, error, refetch } = useBrands();
  const [brandId, setBrandId] = useSelectedBrand((data?.items ?? []).map((brand) => brand.id));

  return (
    <div className="px-4 py-6 sm:px-6">
      <div>
        <h1 className="text-xl font-semibold text-heading">Sender addresses</h1>
        <p className="mt-1 text-sm text-muted">
          The addresses a campaign can come from. Always on the brand's own marketing domain.
        </p>
      </div>

      {!isAdministrator && <ReadOnlyNotice />}

      {isPending ? (
        <div className="mt-5 flex flex-col gap-3">
          <Skeleton className="h-10" />
          <Skeleton className="h-48" />
        </div>
      ) : isError ? (
        <div className="mt-5">
          <ErrorState error={error} onRetry={() => void refetch()} />
        </div>
      ) : data.items.length === 0 ? (
        <div className="mt-5 rounded-lg border border-border bg-surface">
          <EmptyState
            icon={Building2}
            title="No brands yet"
            description="A sender address belongs to a brand, so a brand has to exist first."
          />
        </div>
      ) : (
        <>
          <BrandSelector brands={data.items} selectedId={brandId} onSelect={setBrandId} />

          {brandId !== undefined && (
            <BrandSenders brandId={brandId} isAdministrator={isAdministrator} />
          )}
        </>
      )}
    </div>
  );
}

function BrandSenders({ brandId, isAdministrator }: { brandId: string; isAdministrator: boolean }) {
  const { data, isPending, isError, error, refetch } = useBrand(brandId);
  const [sender, setSender] = useState<{ existing?: SenderIdentitySummary } | null>(null);

  if (isPending) return <Skeleton className="mt-5 h-48" />;

  if (isError) {
    return (
      <div className="mt-5">
        <ErrorState error={error} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="mt-5">
      <Senders
        brand={data}
        isAdministrator={isAdministrator}
        onEdit={(existing) => setSender({ existing })}
        onAdd={() => setSender({})}
      />

      {sender !== null && (
        <SenderForm brand={data} existing={sender.existing} onClose={() => setSender(null)} />
      )}
    </div>
  );
}

function Senders({
  brand,
  isAdministrator,
  onEdit,
  onAdd,
}: {
  brand: BrandDetail;
  isAdministrator: boolean;
  onEdit: (sender: SenderIdentitySummary) => void;
  onAdd: () => void;
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-border bg-surface">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold text-heading">Sender addresses</h2>

        {isAdministrator && (
          <Button variant="secondary" size="sm" onClick={onAdd}>
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            Add an address
          </Button>
        )}
      </div>

      {brand.senderIdentities.length === 0 ? (
        <EmptyState
          icon={AtSign}
          title="No sender addresses"
          description="A campaign cannot send without one. The address is always on this brand's marketing domain — you choose the part before the @."
          action={
            isAdministrator ? (
              <Button variant="primary" onClick={onAdd}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                Add an address
              </Button>
            ) : undefined
          }
        />
      ) : (
        <ul className="divide-y divide-border">
          {brand.senderIdentities.map((sender) => (
            <li key={sender.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-heading">
                  {sender.fromAddress}
                  {!sender.isActive && <Pill tone="neutral">Retired</Pill>}
                  {sender.hasDmarcWarning && <Pill tone="warning">DMARC warning</Pill>}
                </p>

                <p className="mt-0.5 text-xs text-muted">
                  {sender.label} · shows as “{sender.fromName}”
                  {sender.replyToAddress !== null && ` · replies to ${sender.replyToAddress}`}
                </p>
              </div>

              {isAdministrator && (
                <Button
                  variant="ghost"
                  size="sm"
                  // Named, because a page with two buttons both called "Edit" gives a screen-reader
                  // user no way to tell them apart. The visible text is still contained in the
                  // accessible name, which is what WCAG 2.5.3 asks for.
                  aria-label={`Edit ${sender.fromAddress}`}
                  onClick={() => onEdit(sender)}
                >
                  Edit
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function SenderForm({
  brand,
  existing,
  onClose,
}: {
  brand: BrandDetail;
  existing?: SenderIdentitySummary | undefined;
  onClose: () => void;
}) {
  const [label, setLabel] = useState(existing?.label ?? '');
  const [localPart, setLocalPart] = useState(existing?.fromLocalPart ?? '');
  const [fromName, setFromName] = useState(existing?.fromName ?? brand.defaultFromName);
  const [replyToAddress, setReplyToAddress] = useState(existing?.replyToAddress ?? '');
  const [isActive, setIsActive] = useState(existing?.isActive ?? true);
  const [touched, setTouched] = useState(false);

  const save = useSaveSender(brand.id);

  const errors: Record<string, string> = {};

  if (label.trim() === '') errors.label = 'Give it a name you would recognise in a list.';
  if (localPart.trim() === '') errors.localPart = 'Enter the part before the @.';
  else if (!/^[a-zA-Z0-9._%+-]+$/.test(localPart.trim())) {
    errors.localPart = 'Just the part before the @ — no domain, no spaces.';
  }
  if (fromName.trim() === '') errors.fromName = 'Enter the name recipients will see.';

  return (
    <Modal
      title={existing === undefined ? 'Add a sender address' : `Edit ${existing.fromAddress}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>

          <Button type="submit" form="sender-form" variant="primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <form
        id="sender-form"
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          setTouched(true);

          const firstBad = Object.keys(errors)[0];

          if (firstBad !== undefined) {
            document.getElementById(firstBad)?.focus();
            return;
          }

          save.mutate(
            {
              ...(existing === undefined ? {} : { senderIdentityId: existing.id }),
              label: label.trim(),
              fromLocalPart: localPart.trim(),
              fromName: fromName.trim(),
              replyToAddress: replyToAddress.trim() === '' ? null : replyToAddress.trim(),
              isActive,
            },
            { onSuccess: onClose },
          );
        }}
      >
        <Field id="label" label="Name for this address" error={touched ? errors.label : undefined}>
          <input id="label" type="text" value={label} onChange={(e) => setLabel(e.target.value)} className={inputClass} />
        </Field>

        <Field
          id="localPart"
          label="Address"
          error={touched ? errors.localPart : undefined}
          hint="The domain comes from the brand, so an address can never point somewhere unauthenticated."
        >
          <div className="flex items-center">
            <input
              id="localPart"
              type="text"
              value={localPart}
              onChange={(e) => setLocalPart(e.target.value)}
              placeholder="newsletter"
              className={`${inputClass} rounded-r-none`}
            />

            {/* Static, not a field. The domain is the brand's. */}
            <span className="flex h-10 shrink-0 items-center rounded-r-md border border-l-0 border-border bg-surface-2 px-3 text-sm text-muted">
              @{brand.fromDomain}
            </span>
          </div>
        </Field>

        <Field
          id="fromName"
          label="Shown to recipients as"
          error={touched ? errors.fromName : undefined}
        >
          <input
            id="fromName"
            type="text"
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field id="senderReplyTo" label="Reply-to address" hint="Optional. Overrides the brand's.">
          <input
            id="senderReplyTo"
            type="email"
            value={replyToAddress}
            onChange={(e) => setReplyToAddress(e.target.value)}
            className={inputClass}
          />
        </Field>

        <Checkbox
          id="senderIsActive"
          label="Available for new campaigns"
          hint="Turn this off to retire an address. It is not deleted — sent campaigns reference it, and a deleted one would leave those reports unable to say who the mail came from."
          checked={isActive}
          onChange={setIsActive}
        />

        {save.isError && (
          <p role="alert" className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-ink">
            {describeError(save.error).message}
          </p>
        )}
      </form>
    </Modal>
  );
}

